import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHECKS = Object.freeze([
  ['b2b', '/api/internal/foundry/tracks/b2b/workspace'],
  ['consumer', '/api/internal/foundry/tracks/consumer/workspace'],
  ['gamer', '/api/internal/foundry/tracks/gamer/workspace'],
]);

// This is a read-only, manually triggered acceptance aid. It deliberately has
// no URL input and never renders a protected response body or request headers.
export default function FoundryAccessCheck({ user, backendUrl = API_URL }) {
  const [busy, setBusy] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [results, setResults] = useState([]);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    setIdentity(null);
    setResults([]);
    const rows = [];
    let currentRoute = '/api/auth/whoami';
    const record = (row) => {
      rows.push({ ...row, checkedAt: new Date().toISOString() });
      setResults([...rows]);
    };

    try {
      if (!backendUrl) {
        record({ route: '/api/auth/whoami', status: null, code: 'backend_origin_unavailable' });
        return;
      }
      const whoami = await fetch(`${backendUrl}/api/auth/whoami`, {
        method: 'GET', credentials: 'include', cache: 'no-store',
      });
      if (whoami.status !== 200) {
        record({ route: '/api/auth/whoami', status: whoami.status,
          code: whoami.status === 401 ? 'not_authenticated' : 'identity_read_failed' });
        return;
      }
      const actor = await whoami.json();
      const safeActor = {
        email: actor.email,
        tenantId: actor.tenant_id,
        role: actor.role_label,
        isPlatformRoot: actor.is_platform_root,
      };
      setIdentity(safeActor);
      if (!actor.email || actor.email !== user.email ||
          !actor.tenant_id || actor.tenant_id !== user.tenant_id ||
          actor.is_platform_root !== false) {
        record({ route: '/api/auth/whoami', status: 200, code: 'identity_mismatch' });
        return;
      }
      record({ route: '/api/auth/whoami', status: 200, code: 'tenant_identity_verified' });

      for (const [track, route] of CHECKS) {
        currentRoute = route;
        const response = await fetch(`${backendUrl}${route}`, {
          method: 'GET', credentials: 'include', cache: 'no-store',
        });
        if (response.status === 403) {
          const error = await response.json().catch(() => ({}));
          record({ track, route, status: 403, code: error.detail === 'Platform root access required'
            ? 'platform_root_access_required' : 'forbidden_unclassified' });
        } else {
          // In particular, never consume or display an unexpected 2xx body.
          record({ track, route, status: response.status,
            code: response.status === 401 ? 'not_authenticated'
              : response.ok ? 'unexpected_protected_success' : 'unexpected_http_status' });
          return;
        }
      }
    } catch {
      record({ route: currentRoute, status: null, code: 'request_unavailable' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" data-testid="foundry-access-check" style={{ marginTop: 16 }}>
      <div className="panel-header">Foundry access check</div>
      <div className="panel-body">
        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Read-only account check. Runs only when selected and shows status metadata,
          not Foundry data. It does not start discovery.
        </p>
        <button type="button" className="btn btn-outline btn-sm" onClick={run}
          disabled={busy} data-testid="run-foundry-access-check">
          {busy ? 'Checking…' : 'Check access now'}
        </button>
        {identity && (
          <p data-testid="foundry-access-identity" style={{ fontSize: 12 }}>
            {identity.email} · {identity.role || 'Unknown role'} · tenant {identity.tenantId || 'none'}
            {' · '}platform root {String(identity.isPlatformRoot)}
          </p>
        )}
        <div aria-live="polite" data-testid="foundry-access-results">
          {results.map((result, index) => (
            <p key={`${result.route}-${index}`} style={{ fontSize: 12 }}>
              {result.route} · HTTP {result.status ?? 'none'} · {result.code}
              {' · '}{result.checkedAt}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}
